import React, { useState, useContext, useEffect } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import Breadcrumbs from '../Breadcrumbs';
import CommonApi from '../CommonApi';
import { InputNumber } from 'primereact/inputnumber';
import { formatPrice, formatPricePlaceholder, goToCheckout, goToProduct } from '../Helper';
import { Image } from 'primereact/image';
import { useTranslation } from 'react-i18next';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { ScrollTop } from 'primereact/scrolltop';
import Cookies from 'js-cookie';

const Cart = (props) => {
  const [fromCheckout, setFromCheckout] = useState(props.fromCheckout ? true : false);
  const commonCtx = useContext(CommonApi);
  const [deleteID, setDeleteID] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [stockPlantDialogVisible, setStockPlantDialogVisible] = useState(false);
  const [stockPlantData, setStockPlantData] = useState(null);
  const [stockPlantLoading, setStockPlantLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const { t } = useTranslation();
  const hidden = props.fromCheckout ? 'hidden' : '';

  useEffect(() => {
    document.title = commonCtx.title + ' - Cart';
    // Load addresses for stock by plant functionality
  }, []);

  const imageBodyTemplate = (rowData) => {
    rowData = rowData.Product;
    return (
      <Image
        preview
        width="80"
        src={`${rowData?.xp.Images[0]?.ThumbnailUrl}`}
        onError={(e) => (e.target.src = '/nophoto.jpg')}
        alt={`${rowData?.xp.Images[0]?.ThumbnailUrl}`}
        className="imageThumbInCart"
      />
    );
  };

  const getStockByPlant = async (product) => {
    setSelectedProduct(product);
    setStockPlantDialogVisible(true);
    setStockPlantLoading(true);
    setStockPlantData(null);

    try {
      const plantCodes = [];
      if (commonCtx.addresses && commonCtx.addresses.length > 0) {
        commonCtx.addresses.forEach((address) => {
          if (address?.xp?.PlantCode?.Principal) {
            plantCodes.push(address.xp.PlantCode.Principal);
          }
          if (
            address?.xp?.PlantCode?.Alternative &&
            Array.isArray(address.xp.PlantCode.Alternative)
          ) {
            plantCodes.push(...address.xp.PlantCode.Alternative);
          }
        });
      }

      const uniquePlantCodes = [...new Set(plantCodes)];
      const data = await commonCtx.getStockByPlant(product.ID, uniquePlantCodes);
      setStockPlantData(data);
    } catch (error) {
      console.error('Error fetching stock by plant:', error);
      commonCtx.showError(t('Error loading stock information'));
      setStockPlantData([]);
    } finally {
      setStockPlantLoading(false);
    }
  };

  const renderStockPlantDialog = () => {
    return (
      <Dialog
        header={<span className="blueText">{t('StockAvailabilityByPlant')}</span>}
        visible={stockPlantDialogVisible}
        style={{ width: '50vw' }}
        breakpoints={{ '960px': '75vw', '641px': '90vw' }}
        onHide={() => setStockPlantDialogVisible(false)}
      >
        <div className="p-3 pt-0">
          {selectedProduct && (
            <div className="mb-4">
              <h3 className="text-xl font-bold mb-2 blueText">{selectedProduct.Name}</h3>
              <div className="mb-3 align-items-center justify-content-between">
                <p className="text-sm text-600 mb-0">
                  {t('DaycoPartNumber')}: {' ' + selectedProduct?.Name}
                </p>
                {selectedProduct?.xp?.InternalPartNumber !== selectedProduct?.Name && (
                  <div>
                    <p className="text-sm text-600 mb-0">
                      {t('InternalPartNumber')}: {' ' + selectedProduct?.xp?.InternalPartNumber}
                    </p>
                  </div>
                )}
                {selectedProduct?.PriceSchedule?.xp?.AlternativePartNumber &&
                  selectedProduct?.PriceSchedule?.xp?.AlternativePartNumber !==
                    selectedProduct?.Name && (
                    <div>
                      <p className="text-sm text-600 mb-0">
                        {t('CustomerPartNumber')}:{' '}
                        {' ' + selectedProduct?.PriceSchedule?.xp?.AlternativePartNumber}
                      </p>
                    </div>
                  )}
              </div>
            </div>
          )}

          {stockPlantLoading ? (
            <div className="text-center p-5">
              <ProgressSpinner />
              <p className="mt-3">{t('LoadingStockInformation')}</p>
            </div>
          ) : stockPlantData && stockPlantData.plants && stockPlantData.plants.length > 0 ? (
            <div>
              <div className="font-bold text-lg mb-1 pl-2">
                <span className="text-primary">{t('Plant')}:</span>
              </div>
              <div className="grid" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                {stockPlantData.plants.map((plant, index) => {
                  const address = plant.address;

                  return (
                    <div key={index} className="col-12">
                      <div className="border-1 surface-border border-round p-3 pt-2 pb-2">
                        <div className="flex justify-content-between align-items-center">
                          <div className="flex-1">
                            {address ? (
                              <div className="text-md text-600">
                                <p className="font-bold mb-0">
                                  {address.Street1 && address.Street2
                                    ? `${address.City.toUpperCase()}${
                                        address.State ? `, ${address.State.toUpperCase()}` : ''
                                      }${
                                        address.Zip ? ` ${address.Zip.toUpperCase()}` : ''
                                      }, ${address.Country.toUpperCase()}`
                                    : address.Street1
                                    ? `${address.City.toUpperCase()}${
                                        address.State ? `, ${address.State.toUpperCase()}` : ''
                                      }${
                                        address.Zip ? ` ${address.Zip.toUpperCase()}` : ''
                                      }, ${address.Country.toUpperCase()}`
                                    : `${address.City.toUpperCase()}${
                                        address.State ? `, ${address.State.toUpperCase()}` : ''
                                      }${
                                        address.Zip ? ` ${address.Zip.toUpperCase()}` : ''
                                      }, ${address.Country.toUpperCase()}`}
                                </p>
                                <p>{` (${plant.plantCode.toUpperCase()} - ${plant.companyCode.toUpperCase()})`}</p>
                              </div>
                            ) : (
                              <div className="text-md text-600">{t('AddressNotFound')}</div>
                            )}
                          </div>
                          <div className="text-right ml-3">
                            <div className="flex items-start gap-3">
                              <div>
                                <div
                                  className={`text-center product-badge status-${
                                    plant.quantityAvailable > 0 ? 'instock' : 'outofstock'
                                  } mb-1`}
                                >
                                  {plant.quantityAvailable || 0}
                                </div>
                                <div className="text-xs text-600">{t('QtyAvailable')}</div>
                              </div>
                              {plant.quantityArriving !== null && (
                                <div style={{ opacity: 0.6 }}>
                                  <div className="text-center product-badge status-undefinedstock mb-1">
                                    {plant.quantityArriving}
                                  </div>
                                  <div className="text-xs text-600">{t('QtyArriving')}</div>
                                  {plant.dateArriving && (
                                    <div className="text-xs text-600 mt-1">
                                      {new Date(plant.dateArriving).toLocaleDateString()}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-center p-3 blueText">
              <i className="pi pi-info-circle" style={{ fontSize: '4rem' }}></i>
              <p className="mt-2">{t('NoStockInformationAvailable')}</p>
            </div>
          )}
        </div>
      </Dialog>
    );
  };

  const statusBodyTemplate = (rowData) => {
    if (commonCtx.showStock === 'Q') {
      return (
        <div
          className={`product-badge status-${rowData.Product.statusStock} text-center cursor-pointer`}
          onClick={() => getStockByPlant(rowData.Product)}
          title={t('ClickToViewStockByPlant')}
        >
          {t(rowData.Product.labelStock)}
          <i className="pi pi-search-plus ml-2"></i>
        </div>
      );
    }
    return (
      <div className={`product-badge status-${rowData.Product.statusStock} text-center`}>
        {t(rowData.Product.labelStock)}
      </div>
    );
  };

  const qtyTemplate = (rowdata) => {
    return (
      <span className="addQty">
        <span className="p-inputnumber p-component p-inputwrapper p-inputwrapper-filled p-inputnumber-buttons-horizontal">
          {!Cookies.get('isSalesAdmin') && (
            <InputNumber
              showButtons
              buttonLayout="horizontal"
              inputClassName="w-3rem text-center"
              value={rowdata.Quantity}
              inputId={'qty' + rowdata.ID}
              onValueChange={(e) => {
                console.log(e, rowdata?.PriceSchedule);
                if (e.value <= rowdata?.PriceSchedule?.MinQuantity) {
                  document.getElementById('qty' + rowdata.ID).value =
                    rowdata?.PriceSchedule?.MinQuantity;
                  return;
                }
                if (e.value >= rowdata?.PriceSchedule?.MaxQuantity) {
                  document.getElementById('qty' + rowdata.ID).value =
                    rowdata?.PriceSchedule?.MaxQuantity;
                  return;
                }

                if (e.value === 0) {
                  setDeleteDialog(true);
                  setDeleteID(rowdata.ID);
                } else {
                  // check multiplier !
                  console.log(rowdata);
                  if (e.value % rowdata?.PriceSchedule?.xp?.QuantityMultiplier === 0) {
                    commonCtx.modifyLineItem(rowdata, e.value);
                    rowdata.Quantity = e.value;
                  } else {
                    document.getElementById('qty' + rowdata.ID).value = rowdata.Quantity;
                    commonCtx.showError(e.value + ' ' + t('NotMultiple'));
                  }
                }
              }}
              max={
                rowdata?.PriceSchedule?.MaxQuantity ? rowdata?.PriceSchedule?.MaxQuantity : 999999
              }
              min={rowdata?.PriceSchedule?.MinQuantity ? rowdata?.PriceSchedule?.MinQuantity : 1}
              step={
                rowdata?.PriceSchedule?.xp.QuantityMultiplier
                  ? rowdata?.PriceSchedule?.xp.QuantityMultiplier
                  : 1
              }
              decrementButtonClassName="p-button-text"
              incrementButtonClassName="p-button-text"
              incrementButtonIcon="pi pi-plus"
              decrementButtonIcon="pi pi-minus"
            ></InputNumber>
          )}
        </span>
      </span>
    );
  };

  const clickTemplate = (rowData) => {
    return (
      <div
        className="text-xl font-bold pointer blueText  hoverBlack"
        onClick={() => goToProduct(rowData.Product.ID)}
      >
        {rowData.Product.Name}
      </div>
    );
  };

  const priceTemplateUnitPrice = (rowData) => {
    return formatPrice(rowData.UnitPrice, commonCtx.locale);
  };

  const priceTemplateUnitPriceCalculated = (rowData) => {
    return formatPrice(rowData.UnitPriceCalculated, commonCtx.locale);
  };

  const priceTemplateLineTotal = (rowData) => {
    return formatPrice(rowData.LineTotal, commonCtx.locale);
  };

  const priceTemplateLineTotalCalculated = (rowData) => {
    return formatPrice(rowData.LineTotalCalculated, commonCtx.locale);
  };

  const actionBodyTemplate = (rowData) => {
    return (
      <React.Fragment>
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-error"
          onClick={() => {
            setDeleteDialog(true);
            setDeleteID(rowData.ID);
          }}
        />
      </React.Fragment>
    );
  };

  const deleteDialogFooter = (
    <React.Fragment>
      <Button
        label={t('No')}
        icon="pi pi-times"
        className="p-button-text bg-red-500 textWhite"
        onClick={() => setDeleteDialog(false)}
      />
      <Button
        label={t('Yes')}
        icon="pi pi-check"
        className="p-button-text bg-green-500 textWhite"
        onClick={() => {
          commonCtx.removeFromCart(deleteID);
          setDeleteDialog(false);
        }}
      />
    </React.Fragment>
  );

  return (
    <div>
      <Dialog
        visible={deleteDialog}
        style={{ width: '450px' }}
        header={t('Confirm')}
        modal
        footer={() => deleteDialogFooter}
        onHide={() => setDeleteDialog(false)}
      >
        <div className="confirmation-content">
          <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
          {deleteID && <span>{t('AreYouSureYouWantToDelete')} ?</span>}
        </div>
      </Dialog>
      {commonCtx.qtyExceededDialog()}
      {renderStockPlantDialog()}
      <Breadcrumbs leaf={t('Cart')} hidden={fromCheckout}></Breadcrumbs>
      <div className="layout-content grid">
        <div className="col-12 md:col-10">
          <div className="card card-w-title global-sales ui-fluid">
            <DataTable
              loading={commonCtx.loadingCart}
              value={commonCtx.lineItems}
              responsiveLayout="scroll"
              stripedRows
            >
              <Column
                field="Image"
                header={t('Image')}
                hidden={hidden}
                style={{ width: '20%' }}
                body={imageBodyTemplate}
              ></Column>
              <Column
                style={{ width: '15%' }}
                field="Product.Name"
                hidden={hidden}
                header={t('PartNumber')}
                className="text-bold"
                body={clickTemplate}
              ></Column>
              <Column
                style={{ width: '10%' }}
                field="Product.Name"
                hidden={!hidden}
                header={t('PartNumber')}
                className="text-bold"
              ></Column>
              <Column
                field="Product.xp.SubCategory"
                style={{ width: '15%' }}
                header={t('Category')}
              ></Column>
              <Column
                field="Product.Translation"
                hidden={true}
                style={{ width: '20%' }}
                header={t('Name')}
              ></Column>
              <Column
                field="Status"
                style={{ width: '10%' }}
                header={t('Status')}
                hidden={commonCtx.showStock !== 'Y' && commonCtx.showStock !== 'Q'}
                body={statusBodyTemplate}
              ></Column>
              <Column
                field="Quantity"
                hidden={!hidden}
                style={{ width: '10%' }}
                header={t('Quantity')}
              ></Column>
              <Column
                field="Quantity"
                style={{ width: '5%' }}
                hidden={hidden}
                header={t('Quantity')}
                body={qtyTemplate}
              ></Column>
              <Column
                field="UnitPrice"
                style={{ width: '10%' }}
                body={priceTemplateUnitPrice}
                header={t('UnitPrice')}
                hidden={!commonCtx.showPrice}
              ></Column>
              <Column
                field="LineTotal"
                style={{ width: '10%' }}
                body={priceTemplateLineTotal}
                header={t('Subtotal')}
                hidden={!commonCtx.showPrice}
              ></Column>
              <Column
                field="UnitPriceCalculated"
                style={{ width: '10%' }}
                className="text-bold"
                hidden={!hidden}
                body={priceTemplateUnitPriceCalculated}
                header={t('UnitPriceCalculated')}
              ></Column>
              {/* <Column
                field="TaxCalculated"
                style={{ width: '10%' }}
                hidden={!hidden}
                header={t('TaxCost')}
                className="text-bold"
                body={(row) =>
                  row.TaxCalculated != null
                    ? formatPrice(row.TaxCalculated, commonCtx.locale)
                    : ''
                }
              /> */}
              <Column
                field="LineTotalCalculated"
                hidden={!hidden}
                className="text-bold"
                style={{ width: '15%' }}
                body={priceTemplateLineTotalCalculated}
                header={t('Total')}
              ></Column>
              <Column body={actionBodyTemplate} hidden={hidden} exportable={false}></Column>
            </DataTable>
            <ScrollTop />
          </div>
        </div>
        <div className="col-12 md:col-2" hidden={hidden}>
          <div className="card card-w-title global-sales ui-fluid">
            <div className="text-900 font-medium text-xl mb-2">{t('OrderSummary')}</div>
            <div className="text-800 font-medium">
              <b>{commonCtx.lineItems.length}</b> {t('ProductsInCart')}{' '}
            </div>
            <div className="text-500 font-medium mt-2">
              (<b>{commonCtx?.totalProductsInCart}</b> {t('ItemsInTotal')})
            </div>
            <hr className="my-3 mx-0 border-top-1 border-none surface-border" />
            <div className="flex align-items-center">
              {t('Subtotal')}
              <span className="font-bold text-2xl text-900 ml-2">
                {commonCtx.showPrice
                  ? commonCtx?.activeCart?.Subtotal
                    ? formatPrice(commonCtx?.activeCart?.Subtotal, commonCtx.locale)
                    : '-'
                  : formatPricePlaceholder(commonCtx.locale)}
              </span>
            </div>

            {!Cookies.get('isSalesAdmin') && (
              <button
                aria-label={t('Checkout')}
                disabled={commonCtx.lineItems.length === 0 ? true : false}
                className="p-button p-component p-3 w-full mt-4"
                onClick={() => goToCheckout()}
              >
                <span className="p-button-label p-c">{t('GoToCheckout')}</span>
              </button>
            )}
            <button
              aria-label={t('DeleteCart')}
              disabled={commonCtx.LineItemCount === 0 ? true : false}
              className="p-button p-component p-3 w-full mt-4 p-button-error"
              onClick={() => commonCtx.deleteCart()}
            >
              <span className="p-button-label p-c">{t('DeleteCart')}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
